
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Terminal, Fingerprint, Activity, Cpu } from 'lucide-react';

interface IntroProps {
  onComplete: () => void;
}

const Intro: React.FC<IntroProps> = ({ onComplete }) => {
  const [step, setStep] = useState(0);
  const [bootText, setBootText] = useState<string[]>([]);
  const [progress, setProgress] = useState(0);
  const [isExiting, setIsExiting] = useState(false);

  // Sequence Timing (Target: ~8-10 Seconds Total)
  useEffect(() => {
    const sequence = async () => {
      // PHASE 1: BOOT LOGS
      const logs = [
        "BIOS_CHECK_MEM: 64TB OK",
        "INIT_KERNEL_V4.2...",
        "MOUNTING_VIRTUAL_VOLUMES...",
        "LOADING_NEURAL_NET_WEIGHTS...",
        "DECRYPTING_ARCHIVE_2050...",
        "CHECKING_BIO_INTEGRITY...",
        "ESTABLISHING_UPLINK...",
        "ETHICS_PROTOCOL: ACTIVE",
        "SYNCING_DATABASES (MITRE/NIST)...",
        "SYSTEM_READY_FOR_INPUT"
      ];

      for (const log of logs) {
        setBootText(prev => [...prev, log]);
        await new Promise(r => setTimeout(r, 300));
      }

      setStep(1);

      // PHASE 2: LOADING BAR
      const interval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 100) {
            clearInterval(interval);
            return 100;
          }
          return prev + (Math.random() * 2.5); 
        });
      }, 40);

      await new Promise(r => setTimeout(r, 2500));
      
      // PHASE 3: LOGO REVEAL
      setStep(2);
      await new Promise(r => setTimeout(r, 2500)); 

      // PHASE 4: EXIT
      handleExit();
    };

    sequence();
  }, []);

  const handleExit = () => {
    setIsExiting(true);
    setTimeout(onComplete, 800); // Wait for CSS animation
  };

  return createPortal(
    <div 
      className={`fixed inset-0 z-[9999] bg-black text-white font-mono flex flex-col justify-center items-center overflow-hidden transition-transform duration-1000 ease-[cubic-bezier(0.87,0,0.13,1)] ${isExiting ? '-translate-y-full' : 'translate-y-0'}`}
    >
        {/* BACKGROUND NOISE & GRID */}
        <div className="absolute inset-0 opacity-10 pointer-events-none" style={{backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)', backgroundSize: '40px 40px'}}></div>
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] opacity-20 animate-pulse"></div>

        {/* CONTENT CONTAINER */}
        <div className="relative z-10 w-full max-w-2xl px-8">
            
            {/* PHASE 1: TERMINAL LOGS */}
            {step === 0 && (
                <div className="text-xs md:text-sm text-gray-400 space-y-1 h-64 flex flex-col justify-end border-l-2 border-white pl-4 font-mono">
                    {bootText.map((text, i) => (
                        <div key={i} className="animate-fade-in">
                            <span className="text-green-500 mr-2">[{new Date().toLocaleTimeString()}]</span>
                            {text}
                        </div>
                    ))}
                </div>
            )}

            {/* PHASE 2: SYNCHRONIZATION */}
            {step === 1 && (
                <div className="flex flex-col items-center animate-fade-in">
                    <div className="flex items-center gap-12 mb-8 opacity-80">
                        <div className="flex flex-col items-center gap-2">
                            <Fingerprint size={48} className="animate-pulse text-gray-300" />
                            <span className="text-[10px] tracking-[0.3em] uppercase">Biological</span>
                        </div>
                        <Activity size={24} className="animate-spin-slow" />
                        <div className="flex flex-col items-center gap-2">
                            <Cpu size={48} className="animate-pulse text-gray-300" />
                            <span className="text-[10px] tracking-[0.3em] uppercase">Digital</span>
                        </div>
                    </div>
                    
                    <div className="w-full max-w-md h-px bg-gray-800 relative overflow-hidden">
                        <div className="absolute top-0 left-0 h-full bg-white transition-all duration-100 ease-out" style={{ width: `${progress}%` }}></div>
                    </div>
                    <div className="mt-4 text-xs font-bold uppercase tracking-widest flex justify-between w-full max-w-md">
                        <span>Establishing Neural Handshake</span>
                        <span>{Math.round(progress)}%</span>
                    </div>
                </div>
            )}

            {/* PHASE 3: TITLE REVEAL */}
            {step === 2 && (
                <div className="text-center animate-slide-up">
                    <div className="mb-4 flex justify-center">
                        <Terminal size={32} className="text-white" />
                    </div>
                    
                    <h1 className="text-5xl md:text-8xl leading-none mb-2 mix-blend-difference flex flex-col md:flex-row items-center justify-center gap-4">
                        <div className="flex items-baseline gap-1">
                            <span className="font-sans font-extrabold tracking-tighter">CYBER</span>
                            <span className="font-sans font-medium italic tracking-tight">UMANITÀ</span>
                        </div>
                    </h1>
                    
                    <p className="text-xs md:text-sm text-gray-400 uppercase tracking-[0.5em] animate-pulse mt-4">
                        System Ready
                    </p>
                </div>
            )}

        </div>

        {/* FOOTER METADATA */}
        <div className="absolute bottom-8 left-8 text-[10px] text-gray-600 font-bold uppercase tracking-widest hidden md:block">
            Sys_Ver 2.9 // Encrypted Connection
        </div>

        {/* SKIP BUTTON */}
        <button 
            onClick={handleExit}
            className="absolute bottom-8 right-8 z-50 group flex items-center gap-3 px-4 py-2 border border-gray-800 hover:border-white hover:bg-white hover:text-black transition-all duration-300 cursor-pointer bg-black"
        >
            <span className="text-[9px] font-bold uppercase tracking-[0.2em]">
                [ SKIP_INTRO ]
            </span>
            <div className="w-1 h-1 bg-white group-hover:bg-black rounded-full animate-ping"></div>
        </button>
    </div>,
    document.body
  );
};

export default Intro;
